import { createContext, useCallback, useEffect, useReducer, useRef, type ReactNode } from 'react'
import { fetchAPI } from '@/api/client'
import { POLL_INTERVAL_MS } from '@/constants/thresholds'
import type {
  DashboardResponse,
  UserInfo,
  GeoInfo,
  AllocationInfo,
  OverflowPoolInfo,
  GeneralPoolInfo,
  TierInfo,
  OfferInfo,
  CapUnit,
  EverflowOfferRef,
  GiveUpRequest,
  GiveUpResponse,
  OverflowWindowInfo,
  PublicOverflowGeo,
  PermanentRequestGeo,
} from '@/types/api'

interface DashboardState {
  user: UserInfo | null
  offers: OfferInfo[]
  selectedOfferId: number | null
  capUnit: CapUnit
  geos: GeoInfo[]
  allocations: AllocationInfo[]
  overflowPools: OverflowPoolInfo[]
  overflowIsLocked: boolean
  overflowWindow: OverflowWindowInfo | null
  publicOverflowGeos: PublicOverflowGeo[]
  permanentRequestGeos: PermanentRequestGeo[]
  priorityRank: number | null
  priorityTotal: number
  generalPools: GeneralPoolInfo[]
  tiers: TierInfo[]
  everflowOffers: EverflowOfferRef[]
  selectedGeoCode: string | null
  lastSyncAt: Date | null
  isLoading: boolean
  error: string | null
}

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: DashboardResponse }
  | { type: 'FETCH_ERROR'; error: string }
  | { type: 'SELECT_GEO'; geoCode: string | null }
  | { type: 'SELECT_OFFER'; offerId: number }

function reducer(state: DashboardState, action: Action): DashboardState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null }
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        user: action.payload.user,
        offers: action.payload.offers ?? state.offers,
        // Lock in the offer the server resolved (default = first active offer).
        selectedOfferId: state.selectedOfferId ?? action.payload.offer?.id ?? null,
        capUnit: action.payload.offer?.cap_unit ?? 'usd',
        geos: action.payload.geos,
        allocations: action.payload.allocations,
        overflowPools: action.payload.overflow_pools,
        overflowIsLocked: action.payload.overflow_is_locked ?? false,
        overflowWindow: action.payload.overflow_window ?? null,
        publicOverflowGeos: action.payload.public_overflow_geos ?? [],
        permanentRequestGeos: action.payload.permanent_request_geos ?? [],
        priorityRank: action.payload.priority_rank ?? null,
        priorityTotal: action.payload.priority_total ?? 0,
        generalPools: action.payload.general_pools ?? [],
        tiers: action.payload.tiers ?? [],
        everflowOffers: action.payload.everflow_offers ?? [],
        lastSyncAt: new Date(),
        selectedGeoCode:
          state.selectedGeoCode ??
          action.payload.allocations[0]?.geo_code ??
          null,
      }
    case 'FETCH_ERROR':
      return { ...state, isLoading: false, error: action.error }
    case 'SELECT_GEO':
      return { ...state, selectedGeoCode: action.geoCode }
    case 'SELECT_OFFER':
      // Switching offers clears the geo selection (geos differ per offer).
      return { ...state, selectedOfferId: action.offerId, selectedGeoCode: null }
  }
}

const initialState: DashboardState = {
  user: null,
  offers: [],
  selectedOfferId: null,
  capUnit: 'usd',
  geos: [],
  allocations: [],
  overflowPools: [],
  overflowIsLocked: false,
  overflowWindow: null,
  publicOverflowGeos: [],
  permanentRequestGeos: [],
  priorityRank: null,
  priorityTotal: 0,
  generalPools: [],
  tiers: [],
  everflowOffers: [],
  selectedGeoCode: null,
  lastSyncAt: null,
  isLoading: true,
  error: null,
}

export interface DashboardContextValue extends DashboardState {
  selectGeo: (geoCode: string | null) => void
  selectOffer: (offerId: number) => void
  refresh: () => Promise<void>
  giveUpCap: (geoId: number, amount: number) => Promise<void>
}

export const DashboardContext = createContext<DashboardContextValue | null>(null)

/**
 * `initialOfferId` is the offer the route names (`/offer/:offerId`). The dashboard is entered
 * one offer at a time from the offer list, so the very first fetch must already be scoped to
 * that offer — otherwise the provider would load the default offer, then swap.
 */
export function DashboardProvider({
  children,
  initialOfferId = null,
}: {
  children: ReactNode
  initialOfferId?: number | null
}) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    selectedOfferId: initialOfferId,
  })
  const abortRef = useRef<AbortController | null>(null)
  // Mirror the selected offer in a ref so fetchDashboard (and the poll) always
  // requests the current offer without being re-created on every change.
  const selectedOfferRef = useRef<number | null>(initialOfferId)

  const fetchDashboard = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    dispatch({ type: 'FETCH_START' })
    try {
      const offerId = selectedOfferRef.current
      const data = await fetchAPI<DashboardResponse>(
        offerId ? `/api/dashboard?offer=${offerId}` : '/api/dashboard',
        {},
        controller.signal
      )
      dispatch({ type: 'FETCH_SUCCESS', payload: data })
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      dispatch({ type: 'FETCH_ERROR', error: String(err) })
    }
  }, [])

  // Keep the ref in sync once the server resolves/echoes the selected offer.
  useEffect(() => {
    selectedOfferRef.current = state.selectedOfferId
  }, [state.selectedOfferId])

  const selectGeo = useCallback((geoCode: string | null) => {
    dispatch({ type: 'SELECT_GEO', geoCode })
  }, [])

  const selectOffer = useCallback(
    (offerId: number) => {
      if (offerId === selectedOfferRef.current) return
      selectedOfferRef.current = offerId // set before fetch so the request uses it
      dispatch({ type: 'SELECT_OFFER', offerId })
      fetchDashboard()
    },
    [fetchDashboard]
  )

  const giveUpCap = useCallback(
    async (geoId: number, amount: number) => {
      await fetchAPI<GiveUpResponse>('/api/cap/give-up', {
        method: 'POST',
        body: JSON.stringify({ geo_id: geoId, amount } satisfies GiveUpRequest),
      })
      await fetchDashboard()
    },
    [fetchDashboard]
  )

  useEffect(() => {
    fetchDashboard()
    const interval = setInterval(fetchDashboard, POLL_INTERVAL_MS)
    return () => {
      clearInterval(interval)
      abortRef.current?.abort()
    }
  }, [fetchDashboard])

  return (
    <DashboardContext.Provider
      value={{
        ...state,
        selectGeo,
        selectOffer,
        refresh: fetchDashboard,
        giveUpCap,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}
